import React, { useState, useEffect } from 'react';
import { GHANA_TAX_LEVIES } from '../lib/constants';
import { formatMoney } from '../lib/formatters';

/**
 * GhanaTaxPicker
 *
 * Props:
 *   selectedLevies  — string[]  e.g. ['vat', 'nhil']
 *   onChange        — (nextLevies: string[], combinedRate: number) => void
 *   subtotal        — number   used to show per-levy GHS amounts
 *   customRate      — number   value for the "Other" row
 *   onCustomRate    — (rate: number) => void
 */
export default function GhanaTaxPicker({ selectedLevies = [], onChange, subtotal = 0, customRate = 0, onCustomRate }) {
  // Per-levy editable rates — seeded from defaults, persist within the modal session
  const [levyRates, setLevyRates] = useState(
    () => Object.fromEntries(GHANA_TAX_LEVIES.map((l) => [l.value, l.rate]))
  );

  // Reset to defaults whenever the picker is freshly mounted (new invoice modal opened)
  useEffect(() => {
    setLevyRates(Object.fromEntries(GHANA_TAX_LEVIES.map((l) => [l.value, l.rate])));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const combinedLevyRate = GHANA_TAX_LEVIES
    .filter((l) => selectedLevies.includes(l.value))
    .reduce((s, l) => s + (levyRates[l.value] ?? l.rate), 0);

  const toggle = (value) => {
    const next = selectedLevies.includes(value)
      ? selectedLevies.filter((v) => v !== value)
      : [...selectedLevies, value];
    const combined = GHANA_TAX_LEVIES
      .filter((l) => next.includes(l.value))
      .reduce((s, l) => s + (levyRates[l.value] ?? l.rate), 0);
    onChange(next, combined + (customRate || 0));
  };

  const updateRate = (value, raw) => {
    const rate = parseFloat(raw) || 0;
    const updated = { ...levyRates, [value]: rate };
    setLevyRates(updated);
    const combined = GHANA_TAX_LEVIES
      .filter((l) => selectedLevies.includes(l.value))
      .reduce((s, l) => s + (updated[l.value] ?? l.rate), 0);
    onChange(selectedLevies, combined + (customRate || 0));
  };

  const totalRate = combinedLevyRate + (customRate || 0);

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
        Tax / Levies
      </p>

      {GHANA_TAX_LEVIES.map((levy) => {
        const checked      = selectedLevies.includes(levy.value);
        const rate         = levyRates[levy.value] ?? levy.rate;
        const amount       = subtotal * (rate / 100);

        return (
          <div
            key={levy.value}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 border transition-colors ${
              checked
                ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/20'
                : 'border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40'
            }`}
          >
            {/* Checkbox + label */}
            <label className="flex items-center gap-2 cursor-pointer min-w-0 flex-1">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(levy.value)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{levy.label}</span>
            </label>

            {/* Editable rate */}
            <div className="flex items-center gap-1 shrink-0">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={rate}
                onChange={(e) => updateRate(levy.value, e.target.value)}
                className={`w-16 text-right rounded-md border text-xs px-2 py-1 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors ${
                  checked
                    ? 'border-indigo-300 dark:border-indigo-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200'
                    : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500'
                }`}
              />
              <span className="text-xs text-slate-400 w-3">%</span>
            </div>

            {/* GHS amount — only when checked and subtotal > 0 */}
            <span className={`text-xs font-mono w-20 text-right shrink-0 transition-opacity ${checked && subtotal > 0 ? 'text-slate-600 dark:text-slate-400 opacity-100' : 'opacity-0'}`}>
              {formatMoney(amount)}
            </span>
          </div>
        );
      })}

      {/* Other / custom row */}
      <div className="flex items-center gap-3 rounded-lg px-3 py-2 border border-dashed border-slate-200 dark:border-slate-700">
        <span className="text-sm text-slate-500 dark:text-slate-400 flex-1">Other</span>
        <div className="flex items-center gap-1 shrink-0">
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            placeholder="0"
            value={customRate || ''}
            onChange={(e) => {
              const v = parseFloat(e.target.value) || 0;
              onCustomRate(v);
              onChange(selectedLevies, combinedLevyRate + v);
            }}
            className="w-16 text-right rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1 font-mono text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <span className="text-xs text-slate-400 w-3">%</span>
        </div>
        <span className={`text-xs font-mono w-20 text-right shrink-0 transition-opacity ${customRate > 0 && subtotal > 0 ? 'text-slate-600 dark:text-slate-400 opacity-100' : 'opacity-0'}`}>
          {formatMoney(subtotal * ((customRate || 0) / 100))}
        </span>
      </div>

      {/* Combined rate summary */}
      {totalRate > 0 && (
        <div className="flex justify-between items-center px-3 pt-2 text-sm font-semibold text-slate-700 dark:text-slate-300 border-t border-slate-100 dark:border-slate-800 mt-1">
          <span>Combined rate</span>
          <span className="font-mono">{totalRate.toFixed(2)}%</span>
        </div>
      )}
    </div>
  );
}
